import { jwtDecode } from "jwt-decode";

/**
 * Role / permission helpers.
 *
 * The JWT is the whole user document minus the password, so a capability can
 * arrive on any of three fields: `role`, `roles` or `permissions`. Every check
 * in the codebase unions all three — this module is the single implementation
 * of that union so new call sites don't re-derive it inline (and don't repeat
 * the AccountList bug where only `role` was read, hiding the Actions column
 * from a user granted Finance via `permissions`).
 */

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/** Union of role / roles / permissions from the session JWT. Empty on any failure. */
export function getUserRoles() {
  try {
    const token = sessionStorage.getItem("user");
    if (!token) return new Set();
    const user = jwtDecode(token);
    return new Set([
      ...asArray(user?.role),
      ...asArray(user?.roles),
      ...asArray(user?.permissions),
    ]);
  } catch {
    return new Set();
  }
}

export function hasAnyRole(allowed = []) {
  const roles = getUserRoles();
  return allowed.some((r) => roles.has(r));
}

/**
 * Read access to the Accounts area: the account list, an individual
 * accountholder's account, and the statement export actions (download, email,
 * bulk export).
 *
 * `AccountsViewer` is granted through the user's `permissions` array, e.g.
 *   db.users.updateOne({ email }, { $addToSet: { permissions: "AccountsViewer" } })
 * Same mechanism as `CalendarViewer` — there is no editor for `permissions`
 * in AddUser/EditUser yet.
 */
export function canViewAccounts() {
  return hasAnyRole(["AccountsViewer", "Finance", "SuperAdmin"]);
}

/**
 * Money-moving actions on an account — raise an invoice, capture a receipt,
 * issue a credit note. Deliberately NOT granted by AccountsViewer, which is
 * read-and-export only.
 */
export function canManageAccountFinancials() {
  return hasAnyRole(["Finance", "SuperAdmin"]);
}
